import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Etapa, EtapaUpdateDTO, Workflow, WorkflowUpdateDTO } from '../../../types/Workflow.type';
import { WorkflowService } from '../workflow.service';
import { ToastService } from '../../../services/toast.service';
import { UsuarioService } from '../../administracao/clientes/usuarios/usuario.service';
import { Usuario } from '../../../types/Usuario.type';

@Component({
  selector: 'app-workflow-editar',
  templateUrl: './workflow-editar.component.html',
  styleUrl: './workflow-editar.component.css'
})
export class WorkflowEditarComponent implements OnInit {

  loading: boolean = false;
  loadingEtapas: boolean = false;

  workflow: Workflow | null = null;
  etapas: Etapa[] = [];
  usuarios: Usuario[] = [];

  // Campos do formulário
  nome: string = '';
  descricao: string = '';
  aprovacaoAutomatica: boolean = false;
  notificaEmail: boolean = false;

  // Controle de validação
  mostrarErros: boolean = false;

  // Controle de edição de aprovadores
  etapaEmEdicao: string | null = null;
  aprovadoresSelecionados: Usuario[] = [];
  usuarioParaAdicionar: Usuario | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private workflowService: WorkflowService,
    private toastService: ToastService,
    private usuarioService: UsuarioService
  ) {}

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      // Carrega usuários primeiro, depois workflow e etapas
      this.carregarUsuarios(id);
    }
  }

  carregarUsuarios(workflowId: string) {
    this.usuarioService.getAll().subscribe({
      next: (data) => {
        this.usuarios = data;
        // Após carregar usuários, carrega workflow e etapas
        this.carregarWorkflow(workflowId);
        this.carregarEtapas(workflowId);
      },
      error: (erro) => {
        this.toastService.erro('Erro', 'Erro ao carregar usuários: ' + erro.error?.message);
      }
    });
  }

  carregarWorkflow(id: string) {
    this.loading = true;

    this.workflowService.getWorkflows().subscribe({
      next: (workflows) => {
        this.workflow = workflows.find(w => w.id === id) || null;

        if (this.workflow) {
          this.nome = this.workflow.nome;
          this.descricao = this.workflow.descricao;
          this.aprovacaoAutomatica = this.workflow.aprovacaoAutomatica;
          this.notificaEmail = this.workflow.notificaEmail;
        } else {
          this.toastService.erro('Erro', 'Workflow não encontrado');
          this.voltar();
        }
      },
      error: (erro) => {
        this.toastService.erro('Erro', 'Erro ao carregar workflow: ' + erro.error?.message);
        this.loading = false;
      },
      complete: () => {
        this.loading = false;
      }
    });
  }

  carregarEtapas(idWorkflow: string) {
    this.loadingEtapas = true;
    this.etapas = [];

    this.workflowService.getEtapasWorkflow(idWorkflow).subscribe({
      next: (data) => {
        this.etapas = data;
      },
      error: (erro) => {
        this.toastService.erro('Erro', 'Erro ao carregar etapas: ' + erro.error?.message);
        this.loadingEtapas = false;
      },
      complete: () => {
        this.loadingEtapas = false;
      }
    });
  }

  vincularNomesUsuarios() {
    // Os nomes já vêm da API, não precisa vincular
  }

  validarFormulario(): boolean {
    return this.nome.trim().length > 0 && this.descricao.trim().length > 0;
  }

  salvar() {
    if (!this.validarFormulario()) {
      this.mostrarErros = true;
      return;
    }

    if (!this.workflow) {
      return;
    }

    this.loading = true;

    const workflowAtualizado: WorkflowUpdateDTO = {
      id: this.workflow.id,
      entidade: this.workflow.entidade,
      nome: this.nome.trim(),
      descricao: this.descricao.trim(),
      aprovacaoAutomatica: this.aprovacaoAutomatica,
      notificaEmail: this.notificaEmail
    };

    this.workflowService.putWorkflow(workflowAtualizado).subscribe({
      next: () => {
        this.toastService.sucesso('Sucesso', 'Workflow atualizado com sucesso');
        this.voltar();
      },
      error: (erro) => {
        this.toastService.erro('Erro', 'Erro ao atualizar workflow: ' + erro.error?.message);
        this.loading = false;
      },
      complete: () => {
        this.loading = false;
      }
    });
  }

  voltar() {
    this.router.navigate(['/administracao/workflows']);
  }

  formatarNomeEntidade(entidade: string): string {
    return entidade
      .split('_')
      .map(palavra => palavra.charAt(0) + palavra.slice(1).toLowerCase())
      .join(' ')
      .replace('Provisao', 'Provisão')
      .replace('Estornar', 'Estorno');
  }

  toggleEdicaoEtapa(etapa: Etapa) {
    if (this.etapaEmEdicao === etapa.id) {
      this.etapaEmEdicao = null;
      this.aprovadoresSelecionados = [];
      this.usuarioParaAdicionar = null;
    } else {
      this.etapaEmEdicao = etapa.id;

      // Carrega os aprovadores atuais da etapa usando o campo 'usuario'
      this.aprovadoresSelecionados = [];

      if (etapa.aprovadores && etapa.aprovadores.length > 0) {
        etapa.aprovadores.forEach(aprov => {
          const usuario = this.usuarios.find(u => u.id === aprov.usuario);
          if (usuario) {
            this.aprovadoresSelecionados.push(usuario);
          }
        });
      }

      this.usuarioParaAdicionar = null;
    }
  }

  isEtapaEmEdicao(etapaId: string): boolean {
    return this.etapaEmEdicao === etapaId;
  }

  get usuariosDisponiveis(): Usuario[] {
    return this.usuarios.filter(u =>
      !this.aprovadoresSelecionados.some(aprov => aprov.id === u.id)
    );
  }

  adicionarAprovador() {
    if (this.usuarioParaAdicionar && !this.aprovadoresSelecionados.some(a => a.id === this.usuarioParaAdicionar!.id)) {
      this.aprovadoresSelecionados.push(this.usuarioParaAdicionar);
      this.usuarioParaAdicionar = null;
    }
  }

  removerAprovador(usuario: Usuario) {
    this.aprovadoresSelecionados = this.aprovadoresSelecionados.filter(u => u.id !== usuario.id);
  }

  salvarAprovadores(etapa: Etapa) {
    if (!this.workflow) {
      return;
    }

    this.loading = true;

    const etapaAtualizada: EtapaUpdateDTO = {
      id: etapa.id,
      idWorkflow: this.workflow.id,
      ordem: etapa.ordem,
      minimoAprovacao: etapa.minimoAprovacao,
      nome: etapa.nome,
      descricao: etapa.descricao,
      aprovadores: this.aprovadoresSelecionados.map(u => ({
        idEtapa: etapa.id,
        usuario: u.id,
        nomeUsuario: u.nomeUsuario,
        email: u.email
      }))
    };

    this.workflowService.putEtapa(etapaAtualizada).subscribe({
      next: () => {
        this.toastService.sucesso('Sucesso', 'Aprovadores atualizados com sucesso');
        this.etapaEmEdicao = null;
        this.aprovadoresSelecionados = [];
        if (this.workflow) {
          this.carregarEtapas(this.workflow.id);
        }
      },
      error: (erro) => {
        this.toastService.erro('Erro', 'Erro ao atualizar aprovadores: ' + erro.error?.message);
        this.loading = false;
      },
      complete: () => {
        this.loading = false;
      }
    });
  }

  cancelarEdicao() {
    this.etapaEmEdicao = null;
    this.aprovadoresSelecionados = [];
    this.usuarioParaAdicionar = null;
  }
}
